import { EToastVariant } from '@outdoorsyco/bonfire';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import guidedSearchData from '@/assets/guided-search/data.json';
import { useAiSearch } from '@/components/ai-search/AiSearchContext';
import ErrorBoundary from '@/components/ErrorBoundary';
import { GuidedSearchModal, GuidedSearchProvider } from '@/components/guided-search';
import type { TGuidedSearchResponse } from '@/components/guided-search/types';
import { useToast } from '@/hooks/useToast';
import { trackGuidedSearchSelected } from '@/services/analytics/search';

import HelpMeSearchButton from './HelpMeSearchButton';

const HelpMeSearch: React.FC = () => {
  const intl = useIntl();
  const { showToast } = useToast();
  const [showGuidedSearchModal, setShowGuidedSearchModal] = useState(false);
  const { available, enabled } = useAiSearch();

  // Hide the guided-search wizard for renters actively using the new AI search —
  // the AI box does the same "help me narrow it down" job. Kept for the control
  // group (and when AI search is toggled off).
  if (available && enabled) return null;

  return (
    // To prevent flicker, we need to pass the guided search data to the provider.
    // If this changes server side, update the json file in this repo.
    <GuidedSearchProvider
      onApplySelection={() => setShowGuidedSearchModal(false)}
      guidedSearchData={guidedSearchData as unknown as TGuidedSearchResponse}>
      <div className={`flex items-center h-11 pr-2`}>
        <div className={`pr-4 border-r border-neutral-20 md:pr-6`}>
          <HelpMeSearchButton
            onClick={() => {
              setShowGuidedSearchModal(true);
              trackGuidedSearchSelected({ source: 'search_button' });
            }}
          />
        </div>
      </div>

      <ErrorBoundary
        fallBackComponent={null}
        onComponentDidCatch={() => {
          setShowGuidedSearchModal(false);

          showToast(
            EToastVariant.Error,
            intl.formatMessage({ defaultMessage: 'Oops!', id: 'BEbOqj' }),
            intl.formatMessage({
              defaultMessage: 'Something went wrong, please try again.',
              id: '8GvVWZ',
            }),
          );
        }}>
        <GuidedSearchModal
          isOpen={showGuidedSearchModal}
          onClose={() => setShowGuidedSearchModal(false)}
          className="z-[1500]" // to cover header too
        />
      </ErrorBoundary>
    </GuidedSearchProvider>
  );
};

export default HelpMeSearch;
